import { Facet } from '@commercetools/frontend-domain-types/result/Facet';

export const PLP_FACET_ATTRIBUTE_NAMES = ['color', 'size', 'style'] as const;

export type PlpFacetAttributeName = (typeof PLP_FACET_ATTRIBUTE_NAMES)[number];

/** Attributes stored on the product (masterData.attributes), not on variants. */
export const PLP_PRODUCT_LEVEL_ATTRIBUTES = ['style'] as const;

export type PlpProductLevelAttributeName = (typeof PLP_PRODUCT_LEVEL_ATTRIBUTES)[number];

export type PlpFacetUiType = 'color' | 'term';

export interface PlpFacetDefinition {
  attributeName: PlpFacetAttributeName;
  type: PlpFacetUiType;
  label: string;
}

export const PLP_FACET_DEFINITIONS: PlpFacetDefinition[] = [
  { attributeName: 'color', type: 'color', label: 'Color' },
  { attributeName: 'size', type: 'term', label: 'Size' },
  { attributeName: 'style', type: 'term', label: 'Style' },
];

export const isProductLevelPlpAttribute = (attributeName: string): attributeName is PlpProductLevelAttributeName =>
  (PLP_PRODUCT_LEVEL_ATTRIBUTES as readonly string[]).includes(attributeName);

export const getCommercetoolsAttributeSearchPath = (
  attributeName: string,
  attributeConstraint?: string,
): string => {
  if (attributeConstraint === 'SameForAll' || isProductLevelPlpAttribute(attributeName)) {
    return `attributes.${attributeName}`;
  }

  return `variants.attributes.${attributeName}`;
};

export const getCommercetoolsFacetKey = (attributeName: PlpFacetAttributeName): string =>
  getCommercetoolsAttributeSearchPath(attributeName);

export const getPlpFacetAttributeName = (facetKey: string): PlpFacetAttributeName | undefined => {
  if (facetKey.startsWith('attributes.')) {
    const attributeName = facetKey.slice('attributes.'.length);
    return (PLP_FACET_ATTRIBUTE_NAMES as readonly string[]).includes(attributeName)
      ? (attributeName as PlpFacetAttributeName)
      : undefined;
  }

  if (facetKey.startsWith('variants.attributes.')) {
    const attributeName = facetKey.slice('variants.attributes.'.length);
    return (PLP_FACET_ATTRIBUTE_NAMES as readonly string[]).includes(attributeName)
      ? (attributeName as PlpFacetAttributeName)
      : undefined;
  }

  return undefined;
};

export const PLP_FACET_ORDER = PLP_FACET_DEFINITIONS.map((definition) =>
  getCommercetoolsFacetKey(definition.attributeName),
);

export interface PlpFacetConfigurationEntry {
  key: string;
  type: PlpFacetUiType;
  label: string;
}

export const buildDefaultPlpFacetsConfiguration = (): Record<string, PlpFacetConfigurationEntry> =>
  PLP_FACET_DEFINITIONS.reduce<Record<string, PlpFacetConfigurationEntry>>((acc, definition) => {
    const key = getCommercetoolsFacetKey(definition.attributeName);
    acc[key] = { key, type: definition.type, label: definition.label };
    return acc;
  }, {});

const findApiFacetForDefinition = (apiFacets: Facet[], attributeName: PlpFacetAttributeName): Facet | undefined =>
  apiFacets.find((facet) => {
    const facetKey = facet.key ?? facet.identifier;
    if (!facetKey) return false;

    if (facetKey === getCommercetoolsFacetKey(attributeName)) return true;

    return getPlpFacetAttributeName(facetKey) === attributeName;
  });

export const buildPlpFacetsFromCommercetoolsResponse = (apiFacets: Facet[]): Record<string, Facet> => {
  const result: Record<string, Facet> = {};

  for (const definition of PLP_FACET_DEFINITIONS) {
    const facetKey = getCommercetoolsFacetKey(definition.attributeName);
    const apiFacet = findApiFacetForDefinition(apiFacets, definition.attributeName);

    if (!apiFacet || apiFacet.type === 'range') continue;

    result[facetKey] = {
      ...apiFacet,
      key: facetKey,
      identifier: facetKey,
      label: definition.label,
      type: definition.type,
    } as Facet;
  }

  return result;
};
